import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Stamp, Loader2, XCircle, Copy as CopyIcon, ExternalLink, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast, Toaster } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ESTADO_META = {
  solicitado: { label: 'Solicitado', color: 'bg-slate-100 text-slate-700' },
  pago_pendiente_colegio: { label: 'Validado — a pagar', color: 'bg-amber-100 text-amber-800' },
  pagado: { label: 'Pagado', color: 'bg-blue-100 text-blue-800' },
  en_produccion: { label: 'En producción', color: 'bg-purple-100 text-purple-800' },
  listo_retiro: { label: 'Listo para retirar', color: 'bg-green-100 text-green-800' },
  entregado: { label: 'Entregado', color: 'bg-slate-200 text-slate-700' },
};

const TABS_META = [
  { id: '', label: 'Todos' },
  { id: 'solicitado', label: 'Solicitados' },
  { id: 'pago_pendiente_colegio', label: 'A pagar' },
  { id: 'pagado', label: 'Pagados' },
  { id: 'en_produccion', label: 'En producción' },
  { id: 'listo_retiro', label: 'Listos' },
  { id: 'entregado', label: 'Entregados' },
];

export default function ProveedorPanelPublicoPage() {
  const { dashboardToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState('');

  const cargar = async () => {
    try {
      setLoading(true);
      setError(null);
      const r = await axios.get(`${API_URL}/sellos/proveedor-panel/${dashboardToken}`);
      setData(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo cargar el panel');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [dashboardToken]);

  const copiarLink = (accessToken) => {
    const link = `${window.location.origin}/sello/${accessToken}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copiado');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 gap-2">
        <Loader2 className="w-6 h-6 animate-spin" /> Cargando panel…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2"><XCircle className="w-12 h-12 text-red-500" /></div>
            <CardTitle>Panel no disponible</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const pedidosFiltrados = filtro
    ? data.pedidos.filter(p => p.estado === filtro)
    : data.pedidos;

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <Toaster position="top-right" />
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Stamp className="w-6 h-6 text-purple-700" /> Panel del Proveedor — {data.proveedor?.nombre}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Listado de todos los pedidos asignados. Actualizá el estado desde el link individual (columna "Abrir").
            </p>
          </div>
          <Button variant="outline" onClick={cargar} className="w-full sm:w-auto">
            <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
          </Button>
        </div>

        {/* Resumen por estado */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
          {['solicitado','pago_pendiente_colegio','pagado','en_produccion','listo_retiro','entregado'].map(st => (
            <Card key={st} className="p-3">
              <div className="text-xs text-slate-500">{ESTADO_META[st]?.label}</div>
              <div className="text-2xl font-bold text-slate-900">{data.resumen_por_estado?.[st] || 0}</div>
            </Card>
          ))}
        </div>

        {/* Tabs filtro */}
        <div className="flex flex-nowrap gap-2 border-b pb-2 scroll-x-safe">
          {TABS_META.map(t => (
            <button
              key={t.id || 'all'}
              onClick={() => setFiltro(t.id)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm whitespace-nowrap flex-shrink-0 ${
                filtro === t.id ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              data-testid={`prov-panel-tab-${t.id || 'todos'}`}
            >
              {t.label} {t.id ? `(${data.resumen_por_estado?.[t.id] || 0})` : `(${data.total})`}
            </button>
          ))}
        </div>

        {/* Tabla */}
        <Card>
          <CardContent className="p-0 sm:p-4">
            {pedidosFiltrados.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No hay pedidos en este estado.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left">
                    <tr>
                      <th className="py-2 px-3">Matrícula</th>
                      <th className="py-2 px-3">Nombre</th>
                      <th className="py-2 px-3">Evento</th>
                      <th className="py-2 px-3">Fecha</th>
                      <th className="py-2 px-3">Estado</th>
                      <th className="py-2 px-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pedidosFiltrados.map(p => {
                      const meta = ESTADO_META[p.estado] || ESTADO_META.solicitado;
                      return (
                        <tr key={p.id} className="border-t hover:bg-slate-50">
                          <td className="py-2 px-3 font-mono">{p.matricula || '-'}</td>
                          <td className="py-2 px-3">{p.apellido}, {p.nombre}</td>
                          <td className="py-2 px-3 text-slate-600">{p.evento_nombre || '-'}</td>
                          <td className="py-2 px-3 text-slate-500 text-xs">{p.fecha_evento || '-'}</td>
                          <td className="py-2 px-3"><Badge className={`text-xs ${meta.color}`}>{meta.label}</Badge></td>
                          <td className="py-2 px-3 text-right">
                            <div className="inline-flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => copiarLink(p.access_token)} title="Copiar link">
                                <CopyIcon className="w-4 h-4" />
                              </Button>
                              <a
                                href={`/sello/${p.access_token}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-purple-700 hover:text-purple-900 px-2 py-1 rounded hover:bg-purple-50"
                                title="Abrir pedido"
                              >
                                <ExternalLink className="w-4 h-4" />
                                <span className="hidden sm:inline text-xs">Abrir</span>
                              </a>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
